<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ProcessCommodityTable from './ProcessCommodityTable.vue'
import ProcessedCommodityCRUD from '../Commodity/ProcessedCommodityCRUD.vue'
import CommodityListSelector from '../Commodity/CommodityListSelector.vue'
import { todayLocalDate } from '../../services/common/UtilsFunctions'
import { getProcessStatus, PROCESS_STATUS } from '../../services/process/ProcessStatus'
import { summarizeProcess, formatKg, formatPercent, originWarning, positiveInteger } from '../../services/process/ProcessMetrics'
import processRest from '../../services/process/ProcessRestService'
const store = useStore()
const empty = () => ({ id: -1, processNumber: null, referenceName: '', date: todayLocalDate(), obs: '', status: PROCESS_STATUS.DRAFT, naturalCommodities: [], processedCommodities: [] })
const edited = ref(empty())
const form = ref(null)
const saving = ref(false)
const error = ref('')
const confirming = ref(null)
const dialog = computed(() => store.getters['processModule/isDialogActived'])
const has = permission => store.getters['authModule/hasPermission'](permission)
const status = computed(() => edited.value.status)
const isDraft = computed(() => status.value === PROCESS_STATUS.DRAFT)
const canEdit = computed(() => isDraft.value && has(edited.value.id > 0 ? 'plant.processes.update' : 'plant.processes.create'))
const canNotes = computed(() => status.value !== PROCESS_STATUS.CANCELLED && (canEdit.value ||
  has(status.value === PROCESS_STATUS.CLOSED ? 'plant.processes.closed_notes_update' : 'plant.processes.notes_update')))
const canCancel = computed(() => edited.value.id > 0 && [PROCESS_STATUS.DRAFT, PROCESS_STATUS.PENDING, PROCESS_STATUS.APPROVED].includes(status.value) && (has('plant.processes.update') || has('plant.processes.approve')))
const canReport = computed(() => edited.value.id > 0 && (has('plant.processes.update') || has('plant.processes.approve')))
const metrics = computed(() => summarizeProcess(edited.value.naturalCommodities, edited.value.processedCommodities))
const warning = computed(() => originWarning(edited.value.naturalCommodities))
const required = value => Boolean(value) || 'Campo requerido.'
const transition = computed(() => {
  if (isDraft.value && edited.value.id > 0 && has('plant.processes.submit')) return { status: PROCESS_STATUS.PENDING, label: 'Enviar a aprobación', color: 'primary' }
  if (status.value === PROCESS_STATUS.PENDING && has('plant.processes.approve')) return { status: PROCESS_STATUS.APPROVED, label: 'Aprobar parte', color: 'primary' }
  if (status.value === PROCESS_STATUS.APPROVED && has('plant.processes.close')) return { status: PROCESS_STATUS.CLOSED, label: 'Cerrar y actualizar stock', color: 'primary' }
  return null
})
function loadSelected () {
  edited.value = JSON.parse(JSON.stringify({ ...empty(), ...(store.getters['processModule/getSelectedItem'] || {}) }))
  store.dispatch('commodityModule/setSelectedItems', edited.value.naturalCommodities)
}
watch(dialog, async value => {
  if (value) {
    error.value = ''
    confirming.value = null
    loadSelected()
    await nextTick()
    form.value?.resetValidation()
  }
})
watch(() => store.getters['commodityModule/getSelectedItems'], items => {
  if (dialog.value && canEdit.value) edited.value.naturalCommodities = JSON.parse(JSON.stringify(items || []))
})
function updateInputs (items) {
  edited.value.naturalCommodities = items
  store.dispatch('commodityModule/setSelectedItems', items)
}
function showError (cause, fallback) {
  const data = cause.response?.data
  error.value = data?.message || (Array.isArray(data?.errorList) ? data.errorList.join(' ') : fallback)
}
async function persist () {
  if (!(await form.value.validate()).valid) return false
  const saved = await store.dispatch('processModule/addOrEditProcess', edited.value)
  if (saved?.id) {
    await store.dispatch('processModule/getProcessById', saved.id)
    loadSelected()
  }
  await store.dispatch('processModule/getProcesses')
  return true
}
async function save () {
  if (saving.value || !(canEdit.value || canNotes.value)) return
  saving.value = true
  error.value = ''
  try { await persist() } catch (cause) { showError(cause, 'No se pudo guardar. Revise el listado antes de reintentar si hubo un problema de conexión.') }
  finally { saving.value = false }
}
async function changeStatus (target) {
  if (saving.value) return
  saving.value = true
  error.value = ''
  try {
    // Cancellation acts on the saved part, without saving pending form changes.
    if (target !== PROCESS_STATUS.CANCELLED && isDraft.value && canEdit.value && !await persist()) return
    await store.dispatch('processModule/updateStatus', { id: edited.value.id, status: target })
    loadSelected()
    confirming.value = null
    store.commit('setSnackBarStatus', { actived: true, message: `Parte ${getProcessStatus(target).title.toLocaleLowerCase()}.`, color: 'success' })
  } catch (cause) { showError(cause, 'No se pudo cambiar el estado del parte.'); confirming.value = null }
  finally { saving.value = false }
}
async function download () {
  if (!canReport.value || saving.value) return
  saving.value = true
  error.value = ''
  try { await processRest.downloadReport(edited.value.id, edited.value.processNumber) }
  catch (cause) { showError(cause, 'No se pudo descargar el reporte. El reporte utiliza los datos guardados del parte.') }
  finally { saving.value = false }
}
function close () {
  if (saving.value) return
  store.dispatch('processModule/setDialogActived', false)
  store.commit('processModule/setSelectedItemAsDefault')
  store.commit('commodityModule/setSelectedItemAsDefault')
}
</script>

<template>
  <v-dialog :model-value="dialog" max-width="1500" scrollable persistent>
    <v-card>
      <v-card-title class="part-heading">
        <span>{{ edited.id > 0 ? `Parte de proceso ${edited.processNumber}` : 'Nuevo parte de proceso' }}</span>
        <v-chip :color="getProcessStatus(status).color">{{ getProcessStatus(status).title }}</v-chip>
        <v-spacer />
        <v-btn v-if="canReport" variant="text" prepend-icon="download" :disabled="saving" @click="download">Descargar Excel</v-btn>
      </v-card-title>
      <v-card-text>
        <v-alert v-if="error" type="error" variant="tonal" class="mb-3">{{ error }}</v-alert>
        <v-alert v-if="status === PROCESS_STATUS.CANCELLED" type="info" variant="tonal" class="mb-3">Parte cancelado: conserva su historial y no mantiene reservas de stock.</v-alert>
        <v-alert v-else-if="status === PROCESS_STATUS.APPROVED" type="info" variant="tonal" class="mb-3">Parte aprobado. Al cerrarlo se consumirán los kilos de origen y se incorporarán los resultados al stock de planta.</v-alert>
        <v-alert v-else-if="status === PROCESS_STATUS.CLOSED" type="info" variant="tonal" class="mb-3">Parte cerrado: stock actualizado. La mercadería permanece en planta hasta registrarla en Salidas.</v-alert>
        <div class="process-summary mb-4">
          <div><span>Entrada</span><strong>{{ formatKg(metrics.inputKg) }} kg</strong></div>
          <div><span>Resultado registrado</span><strong>{{ formatKg(metrics.outputKg) }} kg</strong></div>
          <div><span>Exportación</span><strong>{{ formatKg(metrics.exportKg) }} kg</strong></div>
          <div><span>Eficiencia de exportación</span><strong>{{ formatPercent(metrics.exportEfficiency) }}</strong></div>
          <div><span>Diferencia sin justificar</span><strong>{{ formatKg(metrics.differenceKg) }} kg</strong></div>
        </div>
        <v-form ref="form" @submit.prevent="save">
          <section class="form-section">
            <v-row>
              <v-col cols="12" md="3"><v-text-field v-model="edited.processNumber" label="Número de parte" type="number" min="1" step="1" :readonly="!canEdit" :rules="[positiveInteger]" /></v-col>
              <v-col cols="12" md="5"><v-text-field v-model="edited.referenceName" label="Referencia" :readonly="!canEdit" :rules="[required, v => v.length <= 45 || 'Máximo 45 caracteres.']" /></v-col>
              <v-col cols="12" md="4"><v-text-field v-model="edited.date" label="Fecha del proceso" type="date" :readonly="!canEdit" :rules="[required]" /></v-col>
            </v-row>
          </section>
          <section class="form-section">
            <div class="part-heading">
              <h2 class="text-h6">Mercadería a procesar</h2><v-spacer />
              <CommodityListSelector v-if="canEdit" process-selection :process-id="edited.id > 0 ? edited.id : null" />
            </div>
            <v-alert v-if="warning" type="warning" variant="tonal" class="mt-3">{{ warning }}</v-alert>
            <ProcessCommodityTable :items="edited.naturalCommodities" inputs :editable="canEdit" @update:items="updateInputs" />
            <p v-if="isDraft" class="text-body-2 mt-2">Al guardar se reservan los kilos elegidos. Puede reducir la cantidad; el saldo queda disponible.</p>
          </section>
          <section class="form-section mt-5">
            <ProcessedCommodityCRUD v-model="edited.processedCommodities" :items="edited.processedCommodities" :readonly="!canEdit" />
            <p class="text-body-2 mt-2">Registre los resultados finales y distribuya manualmente todas las calidades. VOLÁTIL conserva stock y ubicación, pero no cuenta como exportación.</p>
          </section>
          <v-textarea v-model="edited.obs" label="Observaciones" rows="2" auto-grow :readonly="!canNotes" />
          <p v-if="isDraft" class="text-body-2">Puede guardar incompleto. Para cerrar, los kilos utilizados deben coincidir con todos los resultados.</p>
        </v-form>
      </v-card-text>
      <v-card-actions class="part-actions">
        <v-btn variant="text" :disabled="saving" @click="close">Salir sin guardar</v-btn>
        <v-menu v-if="canCancel">
          <template #activator="{ props }"><v-btn v-bind="props" variant="text" :disabled="saving" prepend-icon="more_vert">Otras acciones</v-btn></template>
          <v-list><v-list-item title="Cancelar parte" prepend-icon="cancel" @click="confirming = PROCESS_STATUS.CANCELLED" /></v-list>
        </v-menu>
        <v-spacer />
        <v-btn v-if="status === PROCESS_STATUS.PENDING && has('plant.processes.return_to_draft')" variant="tonal" :disabled="saving" @click="changeStatus(PROCESS_STATUS.DRAFT)">Volver a borrador</v-btn>
        <v-btn v-if="canEdit || canNotes" :variant="transition ? 'tonal' : 'flat'" :color="transition ? 'secondary' : 'primary'" :loading="saving" @click="save">{{ canEdit ? 'Guardar borrador' : 'Guardar observaciones' }}</v-btn>
        <v-btn v-if="transition" color="primary" :disabled="saving" @click="confirming = transition.status">{{ transition.label }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog :model-value="confirming != null" max-width="560" @update:model-value="confirming = null">
    <v-card>
      <v-card-title>{{ confirming === PROCESS_STATUS.CANCELLED ? 'Cancelar parte' : getProcessStatus(confirming).title }}</v-card-title>
      <v-card-text>
        <p v-if="confirming === PROCESS_STATUS.CANCELLED">Se conservará el parte como cancelado y se liberarán todos sus kilos reservados. Los cambios sin guardar se descartarán.</p>
        <p v-else-if="confirming === PROCESS_STATUS.CLOSED">Se confirmarán {{ formatKg(metrics.inputKg) }} kg utilizados y {{ formatKg(metrics.outputKg) }} kg de resultados. Diferencia: {{ formatKg(metrics.differenceKg) }} kg. El stock se actualizará y el contenido quedará bloqueado.</p>
        <p v-else>{{ confirming === PROCESS_STATUS.APPROVED ? 'El parte quedará autorizado para cerrar. Aprobar todavía no transforma el stock.' : 'Se guardará el borrador y se enviará a revisión, conservando sus reservas.' }}</p>
      </v-card-text>
      <v-card-actions><v-btn :disabled="saving" @click="confirming = null">Volver</v-btn><v-spacer /><v-btn :color="confirming === PROCESS_STATUS.CANCELLED ? 'error' : 'primary'" :loading="saving" @click="changeStatus(confirming)">Confirmar</v-btn></v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.part-heading, .part-actions { display: flex; align-items: center; flex-wrap: wrap; gap: 12px; }
.process-summary { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 12px; background: rgb(var(--v-theme-surface-variant)); padding: 16px; }
.process-summary span, .process-summary strong { display: block; }
.process-summary strong { margin-top: 4px; font-size: 1.15rem; }
</style>
